/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';
import { ReactComponent as MessageSquareIcon } from '../../../../assets/icons/iconMessageSquare.svg';
import { ReactComponent as TopbarHomeIcon } from '../../../../assets/topbarHomeIcon.svg';
import { ReactComponent as ListIcon } from '../../../../assets/icons/list.svg';
import { ReactComponent as ProfileIcon } from '../../../../assets/icons/profile.svg';
import { ReactComponent as SettingsIcon } from '../../../../assets/icons/settings.svg';
import { ReactComponent as ExitIcon } from '../../../../assets/icons/exit.svg';
import { HeartIcon } from '../../../../components/ListingActions/ListingActionIcons';

import {
  Avatar,
  ExternalLink,
  InlineTextButton,
  NamedLink,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    // const className = css.staticLink;
    const className = css.navigationLink;
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <ListIcon />
        {/* <span className={css.menuItemBorder} /> */}
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.staticLink}>
      <ListIcon />
      {/* <span className={css.menuItemBorder} /> */}
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
    unreadMessagesLog,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const isAdmin = !!user?.attributes?.profile?.metadata?.isAdmin;

  const extraLinks = customLinks.filter(linkConfig => linkConfig?.route?.name !== "SearchPage").map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    return (
      <div className={css.root}>
        <div className={css.accountLinksWrapper}>
          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <NamedLink
            className={css.navigationLink}
            name="SearchPage"
          >
            <ListIcon />
            <FormattedMessage id="TopbarMobileMenu.viewSearches" />
          </NamedLink>
          <NamedLink
            className={css.navigationLink}
            name="NewListingPage"
          >
            <ListIcon />
            <FormattedMessage id="TopbarMobileMenu.createListing" />
          </NamedLink>
        </div>

        <div className={css.footer}>
          <NamedLink className={css.navigationLink} name="LoginPage">
            <FormattedMessage id="TopbarMobileMenu.loginLink" />
          </NamedLink>

          <NamedLink
            name="ContactPage"
            className={css.applyLink}
            to={{ search: "?variant=apply" }}
          >
            <FormattedMessage id="TopbarMobileMenu.apply" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const hasUnreadMessages = Object.values(unreadMessagesLog).some(i => i == true);

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  return (
    <div className={css.root}>
      <div className={css.accountLinksWrapper}>
        <NamedLink
          className={css.navigationLink}
          name="MyHubPage"
        >
          <TopbarHomeIcon />
          Home
        </NamedLink>

        <NamedLink
          className={css.navigationLink}
          name="FavoriteListingsPage"
        >
          <HeartIcon />
          <FormattedMessage id="TopbarMobileMenu.favourites" />
        </NamedLink>

        <NamedLink
          className={css.navigationLink}
          name="ProfileSettingsPage"
        >
          <SettingsIcon />
          Settings
        </NamedLink>

        <div className={css.customLinksWrapper}>{extraLinks}</div>

        {isAdmin ? <NamedLink
          className={css.navigationLink}
          name="AdminDashboardPage"
        >
          <SettingsIcon />
          <FormattedMessage id="TopbarMobileMenu.AdminDashboardLink" />
        </NamedLink> : null}

        {/* <NamedLink
          className={css.navigationLink}
          name="SearchPage"
        >
          <FormattedMessage id="TopbarMobileMenu.viewSearches" />
        </NamedLink>
        <NamedLink
          className={css.navigationLink}
          name="NewListingPage"
        >
          <FormattedMessage id="TopbarMobileMenu.createListing" />
        </NamedLink> */}
      </div>

      {/* <div className={css.spacer} />
      <div className={css.avatarWrapper}>
        <Avatar
          className={css.avatar}
          user={currentUser}
          wrapperStyle={{}}
        />
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
      </div> */}

      {/* <div className={css.profileContent}>
        <NamedLink
          className={css.navigationLink}
          name="ManageListingsPage"
        >
          <ListIcon />
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink>
        <NamedLink
          className={css.navigationLink}
          name="MyHubPage"
        >
          <ListIcon />
          <FormattedMessage id="TopbarMobileMenu.myHubLink" />
        </NamedLink>
        <NamedLink
          className={css.navigationLink}
          name="ProfileSettingsPage"
        >
          <ProfileIcon />
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>
        <NamedLink
          className={css.navigationLink}
          name="AccountSettingsPage"
        >
          <SettingsIcon />
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        {isAdmin ? <NamedLink
          className={css.navigationLink}
          name="AdminDashboardPage"
        >
          <SettingsIcon />
          <FormattedMessage id="TopbarMobileMenu.AdminDashboardLink" />
        </NamedLink> : null}
      </div> */}

      {/* <NamedLink
        className={css.inbox}
        name="InboxPage"
        params={{ tab: inboxTab }}
      >
        <div className={css.inboxLink}>
          <span className={css.inboxLinkLabel}>
            <MessageSquareIcon />
            {hasUnreadMessages ? <div className={css.notificationDot} /> : null}
          </span>
        </div>
        <FormattedMessage id="TopbarMobileMenu.inboxLink" />
      </NamedLink> */}

      <div className={css.footer}>
        <InlineTextButton rootClassName={css.navigationLink} onClick={onLogout}>
          <ExitIcon />
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
